// StyleSheet for PlateCalcModal. Split out (Track R) — verbatim
// makeStyles. Brings the modal under the 500-line budget; the modal is
// one cohesive concern (plate-loading calculator) so the JSX stays one
// component.
import { StyleSheet } from 'react-native';
import type { useTheme } from './theme';

export function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    safe: { flex: 1 },
    flex: { flex: 1 },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
    },
    cancel: { color: p.ink2, fontSize: t.sizes.base },
    apply: {
      color: p.brand500,
      fontSize: t.sizes.base,
      fontWeight: '700',
    },
    title: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: '600',
    },
    headerSpacer: { width: 56 },
    body: {
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s4,
    },
    inputRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
    },
    inputLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      width: 88,
    },
    input: {
      flex: 1,
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      color: p.ink,
      fontSize: t.sizes.lg,
      fontVariant: ['tabular-nums'],
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
    },
    unit: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      fontWeight: '600',
      width: 32,
      textAlign: 'left',
    },
    sectionLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    barRow: {
      flexDirection: 'row',
      gap: s.s3,
      paddingVertical: s.s2,
    },
    barChip: {
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
    },
    barChipSelected: {
      backgroundColor: p.brand500,
      borderColor: p.brand500,
    },
    barChipPressed: {
      opacity: 0.7,
    },
    barChipText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: '600',
      fontVariant: ['tabular-nums'],
    },
    barChipTextSelected: {
      color: p.inkOnBrand,
    },
    outputCard: {
      backgroundColor: p.surface2,
      borderRadius: r.r3,
      padding: s.s4,
      gap: s.s2,
    },
    outputHeader: {
      color: p.ink,
      fontSize: t.sizes.lg,
      fontWeight: '700',
      letterSpacing: -0.2,
      fontVariant: ['tabular-nums'],
    },
    outputBody: {
      color: p.ink2,
      fontSize: t.sizes.base,
    },
    plateRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: s.s2,
    },
    plateKg: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontVariant: ['tabular-nums'],
      fontWeight: '600',
    },
    platePairs: {
      color: p.ink3,
      fontSize: t.sizes.sm,
    },
    remainder: {
      color: p.warn,
      fontSize: t.sizes.sm,
      marginTop: s.s2,
    },
    landmineHint: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      paddingHorizontal: s.s2,
    },
    toggleInventory: {
      paddingVertical: s.s3,
      alignItems: 'center',
    },
    toggleInventoryText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: '600',
    },
    inventoryCard: {
      backgroundColor: p.surface2,
      borderRadius: r.r3,
      padding: s.s4,
      gap: s.s2,
    },
    inventoryHeader: {
      color: p.ink2,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      marginBottom: s.s2,
    },
    inventoryRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
      paddingVertical: s.s2,
    },
    inventoryKg: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontVariant: ['tabular-nums'],
      fontWeight: '600',
      flex: 1,
    },
    stepButton: {
      width: 36,
      height: 36,
      borderRadius: r.r3,
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    stepButtonPressed: {
      backgroundColor: p.surface2,
    },
    stepButtonText: {
      color: p.ink,
      fontSize: t.sizes.lg,
      fontWeight: '700',
    },
    inventoryPairs: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      width: 80,
      textAlign: 'center',
      fontVariant: ['tabular-nums'],
    },
    signRow: {
      gap: s.s2,
    },
    signChipRow: {
      flexDirection: 'row',
      gap: s.s3,
    },
    signChip: {
      flex: 1,
      paddingVertical: s.s3,
      paddingHorizontal: s.s4,
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      alignItems: 'center',
      justifyContent: 'center',
    },
    signChipSelected: {
      backgroundColor: p.brand500,
      borderColor: p.brand500,
    },
    signChipPressed: {
      opacity: 0.7,
    },
    signChipText: {
      color: p.ink2,
      fontSize: t.sizes.base,
      fontWeight: '700',
      letterSpacing: 0.4,
    },
    signChipTextSelected: {
      color: p.inkOnBrand,
    },
  });
}
